import React, { useState, useEffect, useRef } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const ChatbotWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  // Predefined response patterns
  const responsePatterns = [
  {
    keywords: ['hello', 'hi', 'hey', 'greetings'],
    responses: [
    'Hello! Welcome to JOB Fusion. How can I assist you today?',
    'Hi there! I\'m here to help you with job search, applications, and platform features.',
    'Hey! What can I help you with today?']

  },
  {
    keywords: ['job', 'jobs', 'position', 'opening', 'vacancy'],
    responses: [
    'You can search for jobs by visiting the Job Search page. Use filters like location, skills, and salary to find the perfect match!',
    'To find job openings, go to the Job Search & Application section. We have hundreds of opportunities waiting for you!',
    'Looking for jobs? Check out our Job Search page where you can filter by your preferences and apply directly.']

  },
  {
    keywords: ['apply', 'application', 'submit'],
    responses: [
    'To apply for a job, click on any job card and hit the "Apply Now" button. Make sure your profile is complete!',
    'Applications are easy! Find a job you like, click "Apply", and fill out the application form. Your profile details will be auto-filled.',
    'You can track all your applications in the Dashboard under "Application Status". Apply to jobs directly from the Job Search page.']

  },
  {
    keywords: ['profile', 'resume', 'cv', 'portfolio'],
    responses: [
    'Keep your profile updated! Go to Student Profile Management to add your skills, projects, certifications, and documents.',
    'A complete profile increases your chances! Add your academic details, skills, and upload your resume in the Profile section.',
    'Your profile is your digital resume. Make sure to fill in all sections including skills, projects, and certifications.']

  },
  {
    keywords: ['interview', 'schedule', 'meeting'],
    responses: [
    'You can view and manage all your interviews in the Interview Scheduling section. You\'ll receive notifications for upcoming interviews.',
    'Interview scheduled? Check the Interview Management page for details, time slots, and video call links.',
    'All interview requests and schedules are available in the Interview Scheduling & Management section.']

  },
  {
    keywords: ['help', 'support', 'assistance', 'problem'],
    responses: [
    'I\'m here to help! You can ask me about job search, applications, profile management, interviews, or any platform features.',
    'Need assistance? I can guide you through job searching, application tracking, profile updates, and more. What do you need help with?',
    'Happy to help! Ask me anything about using JOB Fusion - from finding jobs to managing your applications.']

  },
  {
    keywords: ['salary', 'pay', 'compensation', 'package'],
    responses: [
    'Salary information is displayed on each job posting. You can also filter jobs by salary range in the Job Search page.',
    'Check the job details for salary information. Use the salary filter in Job Search to find positions matching your expectations.',
    'Salary ranges are mentioned in job descriptions. You can set your salary expectations in your profile too!']

  },
  {
    keywords: ['employer', 'company', 'recruiter'],
    responses: [
    'You can view company profiles and connect with employers through the platform. Check the employer details on each job posting.',
    'Employers can contact you directly through the Live Chat feature. Make sure your profile is complete to attract recruiters!',
    'Browse company profiles and job postings. You can also message employers directly through our Live Chat system.']

  },
  {
    keywords: ['status', 'track', 'progress'],
    responses: [
    'Track all your application statuses in the Dashboard. You\'ll see which applications are pending, reviewed, or shortlisted.',
    'Your application progress is visible in the Role-Based Dashboard under "Application Status". You\'ll get real-time updates!',
    'Check your Dashboard to see the status of all your applications - from submitted to interview scheduled.']

  },
  {
    keywords: ['chat', 'message', 'contact'],
    responses: [
    'Use the Live Chat feature to communicate with employers, students, and institutions in real-time!',
    'You can message recruiters and other users through the Live Chat & Communication Hub. It supports text, files, and more!',
    'Connect with employers and peers using our Live Chat feature. Access it from the sidebar menu.']

  },
  {
    keywords: ['thank', 'thanks', 'appreciate'],
    responses: [
    'You\'re welcome! Feel free to ask if you need anything else.',
    'Happy to help! Good luck with your job search!',
    'My pleasure! Don\'t hesitate to reach out if you have more questions.']

  },
  {
    keywords: ['bye', 'goodbye', 'see you'],
    responses: [
    'Goodbye! Best of luck with your job applications!',
    'See you later! Feel free to come back anytime you need help.',
    'Take care! Happy job hunting!']

  }];


  // Default fallback responses
  const fallbackResponses = [
  'I\'m not sure I understand. Could you rephrase that? I can help with job search, applications, profiles, and interviews.',
  'That\'s a great question! For specific queries, try asking about jobs, applications, profile management, or interviews.',
  'I\'m here to assist with JOB Fusion features. Try asking about job search, application tracking, or profile updates!',
  'Hmm, I didn\'t quite catch that. I can help you with finding jobs, managing applications, or updating your profile. What would you like to know?'];


  // Sample conversation starter
  const welcomeMessage = {
    id: 'welcome',
    senderId: 'bot',
    senderName: 'JOB Fusion Assistant',
    senderAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_15c3aade0-1770155262437.png",
    senderAvatarAlt: 'JOB Fusion chatbot assistant icon',
    content: 'Hi! I\'m your JOB Fusion assistant. I can help you with:\n\n• Finding and applying for jobs\n• Managing your profile\n• Tracking applications\n• Scheduling interviews\n• Platform features\n\nWhat would you like to know?',
    timestamp: new Date(),
    type: 'bot'
  };

  // Initialize with welcome message
  useEffect(() => {
    if (isOpen && messages?.length === 0) {
      setMessages([welcomeMessage]);
    }
  }, [isOpen]);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // Mock response engine
  const generateResponse = (userMessage) => {
    const lowerMessage = userMessage?.toLowerCase();

    // Find matching pattern
    for (const pattern of responsePatterns) {
      for (const keyword of pattern?.keywords) {
        if (lowerMessage?.includes(keyword)) {
          const randomResponse = pattern?.responses?.[Math.floor(Math.random() * pattern?.responses?.length)];
          return randomResponse;
        }
      }
    }

    // Return fallback if no match
    return fallbackResponses?.[Math.floor(Math.random() * fallbackResponses?.length)];
  };

  const handleSend = () => {
    if (messageInput?.trim()) {
      // Add user message
      const userMessage = {
        id: `user-${Date.now()}`,
        senderId: 'me',
        senderName: 'You',
        content: messageInput,
        timestamp: new Date(),
        type: 'user'
      };

      setMessages((prev) => [...prev, userMessage]);
      setMessageInput('');

      // Show typing indicator
      setIsTyping(true);

      // Simulate bot response delay
      setTimeout(() => {
        setIsTyping(false);

        const botResponse = {
          id: `bot-${Date.now()}`,
          senderId: 'bot',
          senderName: 'JOB Fusion Assistant',
          senderAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_15c3aade0-1770155262437.png",
          senderAvatarAlt: 'JOB Fusion chatbot assistant icon',
          content: generateResponse(messageInput),
          timestamp: new Date(),
          type: 'bot'
        };

        setMessages((prev) => [...prev, botResponse]);
      }, 1000 + Math.random() * 1500); // Random delay between 1-2.5 seconds
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSend();
    }
  };

  const formatTime = (date) => {
    return new Date(date)?.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  // Quick action buttons
  const quickActions = [
  { label: 'Find Jobs', icon: 'Briefcase', query: 'How do I find jobs?' },
  { label: 'Track Applications', icon: 'FileText', query: 'How can I track my applications?' },
  { label: 'Update Profile', icon: 'User', query: 'How do I update my profile?' },
  { label: 'Interview Help', icon: 'Calendar', query: 'Tell me about interview scheduling' }];


  const handleQuickAction = (query) => {
    setMessageInput(query);
    setTimeout(() => handleSend(), 100);
  };

  return (
    <>
      {/* Floating Chat Button */}
      <button
        onClick={handleToggle}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-primary text-primary-foreground rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-200 flex items-center justify-center group"
        aria-label="Open chatbot">

        {isOpen ?
        <Icon name="X" size={24} className="group-hover:rotate-90 transition-transform duration-200" /> :

        <Icon name="MessageCircle" size={24} className="group-hover:scale-110 transition-transform duration-200" />
        }
        {!isOpen &&
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-success rounded-full border-2 border-background animate-pulse" />
        }
      </button>

      {/* Chat Window */}
      {isOpen &&
      <div className="fixed bottom-24 right-6 z-50 w-96 h-[600px] bg-card border border-border rounded-lg shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 duration-300">
          {/* Header */}
          <div className="p-4 bg-primary text-primary-foreground flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 bg-primary-foreground/20 rounded-full flex items-center justify-center">
                  <Icon name="Bot" size={20} />
                </div>
                <span className="absolute bottom-0 right-0 w-3 h-3 bg-success border-2 border-primary rounded-full" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">JOB Fusion Assistant</h3>
                <p className="text-xs opacity-90">Always here to help</p>
              </div>
            </div>
            <button
            onClick={handleClose}
            className="hover:bg-primary-foreground/20 p-1 rounded transition-colors"
            aria-label="Close chat">

              <Icon name="Minimize2" size={18} />
            </button>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-muted/30">
            {messages?.map((message, index) => {
            const isBot = message?.type === 'bot';
            const showAvatar = isBot && (index === 0 || messages?.[index - 1]?.type !== 'bot');

            return (
              <div
                key={message?.id}
                className={`flex items-end gap-2 ${isBot ? 'flex-row' : 'flex-row-reverse'}`}>

                  {isBot &&
                <div className="w-8 h-8 flex-shrink-0">
                      {showAvatar &&
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                          <Icon name="Bot" size={16} className="text-primary" />
                        </div>
                  }
                    </div>
                }
                  <div className={`flex flex-col ${isBot ? 'items-start' : 'items-end'} max-w-[75%]`}>
                    <div
                    className={`px-4 py-2 rounded-lg ${
                    isBot ?
                    'bg-card text-foreground border border-border' :
                    'bg-primary text-primary-foreground'}`
                    }>

                      <p className="text-sm whitespace-pre-wrap break-words">{message?.content}</p>
                    </div>
                    <span className="text-xs text-muted-foreground mt-1 px-2">
                      {formatTime(message?.timestamp)}
                    </span>
                  </div>
                </div>);

          })}

            {/* Typing Indicator */}
            {isTyping &&
          <div className="flex items-end gap-2">
                <div className="w-8 h-8 flex-shrink-0">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="Bot" size={16} className="text-primary" />
                  </div>
                </div>
                <div className="bg-card border border-border px-4 py-3 rounded-lg">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
          }

            {/* Quick Actions (show only when no messages except welcome) */}
            {messages?.length === 1 &&
          <div className="space-y-2 pt-2">
                <p className="text-xs text-muted-foreground text-center">Quick actions:</p>
                <div className="grid grid-cols-2 gap-2">
                  {quickActions?.map((action) =>
              <button
                key={action?.label}
                onClick={() => handleQuickAction(action?.query)}
                className="flex items-center gap-2 p-2 bg-card border border-border rounded-lg hover:bg-accent hover:border-primary transition-all text-left text-xs">

                      <Icon name={action?.icon} size={14} className="text-primary" />
                      <span className="text-foreground">{action?.label}</span>
                    </button>
              )}
                </div>
              </div>
          }

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 border-t border-border bg-card">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <textarea
                value={messageInput}
                onChange={(e) => setMessageInput(e?.target?.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me anything..."
                rows={1}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                style={{ minHeight: '40px', maxHeight: '100px' }}
                disabled={isTyping} />

              </div>
              <Button
              iconName="Send"
              onClick={handleSend}
              disabled={!messageInput?.trim() || isTyping}
              size="icon" />

            </div>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Press Enter to send • Shift+Enter for new line
            </p>
          </div>
        </div>
      }
    </>);

};

export default ChatbotWidget;